import { supabase } from '../config/supabase';
import {
  TrainingProgram,
  TrainingProgramWithCourses,
  Course,
  ProgramCourse
} from '../types';

// Helpers to convert DB rows to types
const toTrainingProgram = (row: Record<string, unknown>): TrainingProgram => ({
  id: row.id as string,
  organizationId: row.organization_id as string,
  name: row.name as string,
  description: row.description as string | undefined,
  estimatedDurationHours: row.estimated_duration_hours as number | undefined,
  isActive: row.is_active as boolean,
  createdAt: new Date(row.created_at as string),
  updatedAt: new Date(row.updated_at as string)
});

const toCourse = (row: Record<string, unknown>): Course => ({
  id: row.id as string,
  lmsCourseId: row.lms_course_id as string,
  name: row.name as string,
  description: row.description as string | undefined,
  durationMinutes: row.duration_minutes as number | undefined,
  createdAt: new Date(row.created_at as string)
});

const toProgramCourse = (row: Record<string, unknown>): ProgramCourse => ({
  id: row.id as string,
  programId: row.program_id as string,
  courseId: row.course_id as string,
  orderIndex: row.order_index as number,
  isMandatory: row.is_mandatory as boolean,
  course: row.course ? toCourse(row.course as Record<string, unknown>) : undefined
});

export const trainingProgramsService = {
  // =====================================================
  // TRAINING PROGRAMS
  // =====================================================

  // Get all programs for an organization
  async getByOrganization(organizationId: string): Promise<TrainingProgram[]> {
    const { data, error } = await supabase
      .from('training_programs')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');

    if (error) {
      console.error('Error fetching training programs:', error);
      throw new Error('Impossible de charger les programmes de formation');
    }

    return (data || []).map(toTrainingProgram);
  },

  // Get all programs with their courses
  async getByOrganizationWithCourses(organizationId: string): Promise<TrainingProgramWithCourses[]> {
    const { data: programs, error } = await supabase
      .from('training_programs')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');

    if (error) {
      console.error('Error fetching training programs:', error);
      throw new Error('Impossible de charger les programmes de formation');
    }

    // Get courses for each program
    const programsWithCourses: TrainingProgramWithCourses[] = await Promise.all(
      (programs || []).map(async (programRow) => {
        const program = toTrainingProgram(programRow);

        const { data: programCourses } = await supabase
          .from('program_courses')
          .select(`
            *,
            course:courses(*)
          `)
          .eq('program_id', program.id)
          .order('order_index');

        return {
          ...program,
          courses: (programCourses || []).map(toProgramCourse)
        };
      })
    );

    return programsWithCourses;
  },

  // Get single program by ID
  async getById(id: string): Promise<TrainingProgram | null> {
    const { data, error } = await supabase
      .from('training_programs')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching training program:', error);
      throw new Error('Impossible de charger le programme');
    }

    return data ? toTrainingProgram(data) : null;
  },

  // Get program with courses
  async getWithCourses(id: string): Promise<TrainingProgramWithCourses | null> {
    const { data: programRow, error } = await supabase
      .from('training_programs')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching training program:', error);
      throw new Error('Impossible de charger le programme');
    }

    if (!programRow) return null;

    const program = toTrainingProgram(programRow);

    const { data: programCourses } = await supabase
      .from('program_courses')
      .select(`
        *,
        course:courses(*)
      `)
      .eq('program_id', id)
      .order('order_index');

    return {
      ...program,
      courses: (programCourses || []).map(toProgramCourse)
    };
  },

  // Find program by name or create it
  async getOrCreateByName(organizationId: string, name: string): Promise<TrainingProgram> {
    // First try to find existing program
    const { data: existing } = await supabase
      .from('training_programs')
      .select('*')
      .eq('organization_id', organizationId)
      .ilike('name', name)
      .maybeSingle();

    if (existing) {
      return toTrainingProgram(existing);
    }

    // Create new program
    const { data, error } = await supabase
      .from('training_programs')
      .insert({
        organization_id: organizationId,
        name: name,
        is_active: true
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating training program:', error);
      throw new Error('Impossible de créer le programme');
    }

    return toTrainingProgram(data);
  },

  // Create training program
  async create(program: Omit<TrainingProgram, 'id' | 'createdAt' | 'updatedAt'>): Promise<TrainingProgram> {
    const { data, error } = await supabase
      .from('training_programs')
      .insert({
        organization_id: program.organizationId,
        name: program.name,
        description: program.description,
        estimated_duration_hours: program.estimatedDurationHours,
        is_active: program.isActive ?? true
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating training program:', error);
      throw new Error('Impossible de créer le programme');
    }

    return toTrainingProgram(data);
  },

  // Update training program
  async update(id: string, updates: Partial<Omit<TrainingProgram, 'id' | 'createdAt' | 'updatedAt'>>): Promise<TrainingProgram> {
    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString()
    };

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.estimatedDurationHours !== undefined) updateData.estimated_duration_hours = updates.estimatedDurationHours;
    if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

    const { data, error } = await supabase
      .from('training_programs')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating training program:', error);
      throw new Error('Impossible de mettre à jour le programme');
    }

    return toTrainingProgram(data);
  },

  // Delete training program
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('training_programs')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting training program:', error);
      throw new Error('Impossible de supprimer le programme');
    }
  },

  // =====================================================
  // COURSES
  // =====================================================

  // Get all courses
  async getAllCourses(): Promise<Course[]> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching courses:', error);
      throw new Error('Impossible de charger les cours');
    }

    return (data || []).map(toCourse);
  },

  // Get course by ID
  async getCourseById(id: string): Promise<Course | null> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching course:', error);
      throw new Error('Impossible de charger le cours');
    }

    return data ? toCourse(data) : null;
  },

  // Get or create course by LMS ID
  async getOrCreateCourse(lmsCourseId: string, name: string, description?: string, durationMinutes?: number): Promise<Course> {
    // First try to find existing course
    const { data: existing } = await supabase
      .from('courses')
      .select('*')
      .eq('lms_course_id', lmsCourseId)
      .single();

    if (existing) {
      return toCourse(existing);
    }

    // Create new course
    const { data, error } = await supabase
      .from('courses')
      .insert({
        lms_course_id: lmsCourseId,
        name,
        description,
        duration_minutes: durationMinutes
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating course:', error);
      throw new Error('Impossible de créer le cours');
    }

    return toCourse(data);
  },

  // Create course
  async createCourse(course: Omit<Course, 'id' | 'createdAt'>): Promise<Course> {
    const { data, error } = await supabase
      .from('courses')
      .insert({
        lms_course_id: course.lmsCourseId,
        name: course.name,
        description: course.description,
        duration_minutes: course.durationMinutes
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating course:', error);
      if (error.code === '23505') {
        throw new Error('Un cours avec cet ID LMS existe déjà');
      }
      throw new Error('Impossible de créer le cours');
    }

    return toCourse(data);
  },

  // Update course
  async updateCourse(id: string, updates: Partial<Omit<Course, 'id' | 'createdAt'>>): Promise<Course> {
    const updateData: Record<string, unknown> = {};

    if (updates.lmsCourseId !== undefined) updateData.lms_course_id = updates.lmsCourseId;
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.durationMinutes !== undefined) updateData.duration_minutes = updates.durationMinutes;

    const { data, error } = await supabase
      .from('courses')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating course:', error);
      throw new Error('Impossible de mettre à jour le cours');
    }

    return toCourse(data);
  },

  // Delete course
  async deleteCourse(id: string): Promise<void> {
    const { error } = await supabase
      .from('courses')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting course:', error);
      throw new Error('Impossible de supprimer le cours');
    }
  },

  // =====================================================
  // PROGRAM COURSES (associations)
  // =====================================================

  // Add course to program
  async addCourseToProgram(programId: string, courseId: string, orderIndex: number, isMandatory: boolean = true): Promise<ProgramCourse> {
    const { data, error } = await supabase
      .from('program_courses')
      .insert({
        program_id: programId,
        course_id: courseId,
        order_index: orderIndex,
        is_mandatory: isMandatory
      })
      .select(`
        *,
        course:courses(*)
      `)
      .single();

    if (error) {
      console.error('Error adding course to program:', error);
      throw new Error('Impossible d\'ajouter le cours au programme');
    }

    return toProgramCourse(data);
  },

  // Remove course from program
  async removeCourseFromProgram(programCourseId: string): Promise<void> {
    const { error } = await supabase
      .from('program_courses')
      .delete()
      .eq('id', programCourseId);

    if (error) {
      console.error('Error removing course from program:', error);
      throw new Error('Impossible de retirer le cours du programme');
    }
  },

  // Update course order in program
  async updateCourseOrder(programCourseId: string, newOrderIndex: number): Promise<void> {
    const { error } = await supabase
      .from('program_courses')
      .update({ order_index: newOrderIndex })
      .eq('id', programCourseId);

    if (error) {
      console.error('Error updating course order:', error);
      throw new Error('Impossible de mettre à jour l\'ordre');
    }
  },

  // Reorder all courses in a program
  async reorderCourses(programId: string, courseIds: string[]): Promise<void> {
    // Update each course with new order
    const updates = courseIds.map((courseId, index) =>
      supabase
        .from('program_courses')
        .update({ order_index: index })
        .eq('program_id', programId)
        .eq('course_id', courseId)
    );

    await Promise.all(updates);
  },

  // Get courses for a program
  async getProgramCourses(programId: string): Promise<ProgramCourse[]> {
    const { data, error } = await supabase
      .from('program_courses')
      .select(`
        *,
        course:courses(*)
      `)
      .eq('program_id', programId)
      .order('order_index');

    if (error) {
      console.error('Error fetching program courses:', error);
      throw new Error('Impossible de charger les cours du programme');
    }

    return (data || []).map(toProgramCourse);
  }
};
